import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSignedUrl, getBucketFromPath } from "@/lib/storageUtils";
import {
  uploadFileToStorage,
  MAX_UPLOAD_BYTES,
  FILE_TOO_LARGE_MESSAGE,
} from "@/lib/resumableUpload";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Upload, 
  FileText, 
  Trash2, 
  Download, 
  Eye,
  Loader2,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Document, Page, pdfjs } from 'react-pdf';

// Configure pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const DOCUMENT_TYPES = [
  { value: "constitution", label: "Constitution" },
  { value: "banking_minutes", label: "Banking Minutes" },
  { value: "operating_agreement", label: "Operating Agreement" },
  { value: "ein_letter", label: "EIN Letter" },
  { value: "other", label: "Other Document" },
];

const MAX_FILE_SIZE = MAX_UPLOAD_BYTES;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

type JourneyDocument = {
  id: string;
  user_id: string;
  document_type: string;
  document_name: string;
  file_url: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  stage_context: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export function JourneyDocumentUpload() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string>("constitution");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  
  // Preview dialog state
  const [previewDoc, setPreviewDoc] = useState<JourneyDocument | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  // PDF pagination state
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  // Fetch user's uploaded documents
  const { data: documents, isLoading } = useQuery({
    queryKey: ["journey-documents", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("user_journey_documents")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as JourneyDocument[];
    },
    enabled: !!user?.id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (doc: JourneyDocument) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("documents")
        .remove([doc.file_path]);

      if (storageError) {
        console.error("Storage delete error:", storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from("user_journey_documents")
        .delete()
        .eq("id", doc.id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journey-documents"] });
      toast({
        title: "Document deleted",
        description: "Your document has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Please upload a PDF, JPG, or PNG file.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return FILE_TOO_LARGE_MESSAGE;
    }
    return null;
  };

  const uploadFile = async (file: File) => {
    if (!user?.id) return;

    const error = validateFile(file);
    if (error) {
      toast({
        title: "Invalid file",
        description: error,
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadPercent(0);

    try {
      // Create unique file path
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `journey/${user.id}/${selectedType}/${timestamp}_${sanitizedName}`;

      // Large scans go up in chunks so a slow line can resume instead of
      // failing the whole upload.
      await uploadFileToStorage({
        bucket: "documents",
        path: filePath,
        file,
        onProgress: setUploadPercent,
      });

      // Buckets are private — store path as reference

      // Save to database
      const { error: dbError } = await supabase
        .from("user_journey_documents")
        .insert({
          user_id: user.id,
          document_type: selectedType,
          document_name: file.name,
          file_url: filePath,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          stage_context: "DOCS_DELIVERED",
        });

      if (dbError) throw dbError;

      queryClient.invalidateQueries({ queryKey: ["journey-documents"] });
      toast({
        title: "Document uploaded",
        description: `${file.name} has been saved securely.`,
      });
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description:
          err?.message ||
          "Your file did not save. Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadPercent(0);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) uploadFile(file);
    },
    [selectedType, user?.id]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = ""; // Reset input
  };

  // Blob-based preview handler
  const handlePreview = async (doc: JourneyDocument) => {
    setPreviewDoc(doc);
    setIsLoadingPreview(true);
    setPageNumber(1);
    setNumPages(0);
    try {
      const bucket = getBucketFromPath(doc.file_path);
      const signedUrl = await getSignedUrl(bucket, doc.file_path || doc.file_url);
      if (!signedUrl) throw new Error('Could not generate preview URL');
      const response = await fetch(signedUrl, { cache: 'no-store' });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
    } catch (error) {
      toast({
        title: "Preview failed",
        description: "Unable to load document preview.",
        variant: "destructive",
      });
      setPreviewDoc(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // Blob-based download handler
  const handleDownload = async (doc: JourneyDocument) => {
    try {
      const bucket = getBucketFromPath(doc.file_path);
      const signedUrl = await getSignedUrl(bucket, doc.file_path || doc.file_url);
      if (!signedUrl) throw new Error('Could not generate download URL');
      const response = await fetch(signedUrl, { cache: 'no-store' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      toast({ title: "Download started" });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Unable to download document.",
        variant: "destructive",
      });
    }
  };

  const closePreview = () => {
    setPreviewDoc(null);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPageNumber(1);
    setNumPages(0);
  };

  const getDocumentTypeLabel = (type: string) => {
    return DOCUMENT_TYPES.find((t) => t.value === type)?.label || type;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <>
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Store Your Documents Safely
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Document Type Selector */}
          <div className="space-y-2">
            <Label htmlFor="document-type">Document Type</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger id="document-type" className="w-full md:w-64">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"}
              ${isUploading ? "opacity-50 pointer-events-none" : "cursor-pointer hover:border-primary/50"}
            `}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              disabled={isUploading}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  <span className="text-muted-foreground">
                    {uploadPercent > 0 && uploadPercent < 100
                      ? `Saving your file, ${uploadPercent}% done`
                      : "Saving your file"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Large scans can take a few minutes. Keep this page open.
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-muted-foreground" />
                  <span className="font-medium">Drag & drop files here</span>
                  <span className="text-sm text-muted-foreground">or click to browse</span>
                  <span className="text-xs text-muted-foreground">
                    PDF, JPG, PNG (up to 100 MB)
                  </span>
                </div>
              )}

            </label>
          </div>

          {/* Uploaded Documents List */}
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : documents && documents.length > 0 ? (
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground">
                Your Uploaded Documents ({documents.length})
              </h4>
              <div className="divide-y rounded-lg border">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                        {doc.mime_type === "application/pdf" ? (
                          <FileText className="w-4 h-4 text-primary" />
                        ) : (
                          <FileText className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">
                          {doc.document_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {getDocumentTypeLabel(doc.document_type)}
                          {doc.file_size && ` • ${formatFileSize(doc.file_size)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handlePreview(doc)}
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Document</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{doc.document_name}"?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(doc)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground text-sm">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
              No documents uploaded yet. Upload your scanned documents above.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewDoc?.document_name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto">
            {isLoadingPreview ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : previewBlobUrl && previewDoc ? (
              previewDoc.mime_type === 'application/pdf' ? (
                <div className="flex flex-col items-center">
                  <Document
                    file={previewBlobUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex justify-center items-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    }
                    error={
                      <div className="text-center py-12 text-destructive">
                        Failed to load PDF. Please try downloading instead.
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={pageNumber} 
                      width={700}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                  {numPages > 1 && (
                    <div className="flex items-center gap-4 mt-4 pb-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                        disabled={pageNumber <= 1}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {pageNumber} of {numPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                        disabled={pageNumber >= numPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <img 
                  src={previewBlobUrl} 
                  alt={previewDoc.document_name} 
                  className="max-w-full h-auto mx-auto rounded-lg"
                />
              )
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
